import React, { useEffect, useState } from 'react';
import { useLanguage } from '../state/language/LanguageProvider';
import { PublicHeader } from '@/components/public/PublicHeader';
import { PublicLegalLinks } from '@/components/public/PublicLegalLinks';
import { WhatIsTMOSContent } from '@/components/public/WhatIsTMOSContent';
import PlanningChatPage from '@/pages/ai/PlanningChatPage';
import publicApi from '@/services/publicApi';

/**
 * Public landing: Hero → Approach/How → Impact → CTA → Who.
 * Try AI opens on demand (Ramp-style CTA beside Get started for free).
 */
export const PublicLandingPage: React.FC = () => {
  const { t } = useLanguage();
  const [homepageContent, setHomepageContent] = useState<Record<string, string>>({});
  const [tryAiOpen, setTryAiOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const homeRes = await publicApi.get('/public/homepage-content');
        if (homeRes.data?.success) {
          setHomepageContent(homeRes.data.data || {});
        }
      } catch (error) {
        console.error('Error loading landing content:', error);
      }
    };
    load();
  }, []);

  // Deep-link: /#try-ai or ?tryAi=1 opens the planner panel
  useEffect(() => {
    const openFromHash = () => {
      if (window.location.hash === '#try-ai') {
        setTryAiOpen(true);
      }
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-[var(--color-secondary-bg)]">
      <PublicHeader />

      <main className="flex-1 bg-[var(--color-secondary-bg)]">
        <div className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
          <WhatIsTMOSContent
            homepageContent={homepageContent}
            t={t}
            compact
            tryAiOpen={tryAiOpen}
            onTryAiOpenChange={(open) => {
              setTryAiOpen(open);
              if (open && window.location.hash !== '#try-ai') {
                window.history.replaceState(null, '', '#try-ai');
              }
            }}
            tryAiPanel={
              <div className="flex min-h-[min(70vh,780px)] flex-col overflow-hidden bg-[var(--color-secondary-bg)]">
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--color-secondary-bg)]">
                  <PlanningChatPage embedded />
                </div>
              </div>
            }
          />
        </div>
      </main>

      {/* Slim legal strip — standard placement for Privacy / Terms / Contact */}
      <div className="border-t border-[var(--color-border)] bg-[var(--color-surface-bg)] py-4">
        <PublicLegalLinks muted />
      </div>
    </div>
  );
};
